import { Avatar, Tooltip } from '@affine/component';
import { PublicUserLabel } from '@affine/core/modules/cloud/views/public-user';
import clsx from 'clsx';
import { type CSSProperties, useMemo } from 'react';

import * as styles from './document-presence-avatar-stack.css';
import { carriesOwnName } from './named-participant';

export function DocumentPresenceAvatarStack({
  userIds,
  userNames,
  scopedIdentities = false,
  moderatorUserId,
  size = 16,
  maxVisible = 4,
  className,
  showTooltip = true,
}: {
  userIds: readonly string[] | null | undefined;
  userNames?: ReadonlyMap<string, string>;
  scopedIdentities?: boolean;
  moderatorUserId?: string;
  size?: number;
  maxVisible?: number;
  className?: string;
  showTooltip?: boolean;
}) {
  const ordered = useMemo(() => {
    const unique = Array.from(new Set(userIds ?? []));
    return moderatorUserId && unique.includes(moderatorUserId)
      ? [moderatorUserId, ...unique.filter(id => id !== moderatorUserId)]
      : unique;
  }, [moderatorUserId, userIds]);

  if (ordered.length === 0) return null;

  const visible = ordered.slice(0, maxVisible);
  const overflowCount = ordered.length - visible.length;
  const style = {
    '--document-presence-size': `${size}px`,
    '--document-presence-overlap': `${Math.max(3, Math.round(size * 0.22))}px`,
  } as CSSProperties;

  return (
    <div
      className={clsx(styles.stack, className)}
      style={style}
      data-testid="doc-presence-avatar-stack"
    >
      {visible.map(userId => (
        <span
          key={userId}
          className={clsx(
            styles.avatar,
            userId === moderatorUserId && styles.moderator
          )}
          style={{ width: size, height: size }}
        >
          {(scopedIdentities || carriesOwnName(userId)) &&
          userNames?.has(userId) ? (
            <Tooltip content={showTooltip ? userNames.get(userId) : null}>
              <Avatar
                name={userNames.get(userId)!}
                size={size}
                avatarTooltipOptions={{ content: null }}
              />
            </Tooltip>
          ) : scopedIdentities || carriesOwnName(userId) ? null : (
            <PublicUserLabel
              id={userId}
              size={size}
              showName={false}
              showTooltip={showTooltip}
            />
          )}
        </span>
      ))}
      {overflowCount > 0 ? (
        <span className={styles.overflow}>+{overflowCount}</span>
      ) : null}
    </div>
  );
}
